import {
  Box,
  Button,
  Center,
  Divider,
  Grid,
  Group,
  Stack,
  TextProps,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconMessage } from "@tabler/icons-react";
import { Link } from "gatsby";
import React, { useMemo } from "react";

import { Card, ContactUsDrawer, Text, Title } from "@components";

import iconFacebook from "../../images/icon-facebook.svg";
import iconLinkedin from "../../images/icon-linkedin.svg";
import logoIcon from "../../images/logo-icon.svg";
import logoVertical from "../../images/logo-vertical.svg";

const copyrightTextProps = {
  size: "14px",
  fw: "500",
  c: "fontDarkSecondary",
} as TextProps;

const contactInfo = [
  {
    label: "Email",
    value: "info@airtightdesign.com",
    href: "mailto:info@airtightdesign.com",
  },
  {
    label: "Phone",
    value: "(404) 594-5520",
    href: "tel:4045945520",
  },
  {
    label: "Address",
    value: (
      <>
        1777 Ellsworth Industrial Blvd NW
        <br />
        Suite B
        <br />
        Atlanta, GA 30318
      </>
    ),
  },
];

export function Footer() {
  const [contactUsOpened, { open: openContactUs, close: closeContactUs }] =
    useDisclosure(false);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <>
      <Card
        bg="layoutDark"
        mt="breathe"
        px={{ base: "xl", md: "xl", lg: "xxl" }}
        py={{ base: "xl", md: "xl", lg: "xxxl" }}
      >
        <Grid
          w="100%"
          align="center"
          justify="space-between"
          gutter={{ base: "xl", md: "md", lg: "xl" }}
        >
          <Grid.Col span={12} hiddenFrom="md">
            <Center>
              <img
                src={logoIcon}
                alt="Airtight Design"
                width={60}
                style={{ display: "block" }}
              />
            </Center>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4, lg: 3.5 }} visibleFrom="md">
            <Stack justify="center" gap="lg">
              <Box ta={{ base: "center", md: "left" }}>
                <Title
                  order={3}
                  c="fontLight"
                  mb={{ base: "md", md: "sm", lg: "md" }}
                >
                  Let&apos;s&nbsp;talk.
                </Title>

                <Text
                  size="xs"
                  c="fontLightSecondary"
                  maw={{ base: 290, md: 250, lg: 290 }}
                  mb={{ base: "lg", md: "md", lg: "lg" }}
                  mx={{ base: "auto", md: 0 }}
                >
                  Have an idea, project, or challenge you’d like to explore?
                  We’d love to hear about it.
                </Text>

                <Button
                  fw="600"
                  size="xs"
                  px="md"
                  variant="glass-square"
                  color="brandPrimary"
                  leftSection={
                    <Box mt={2}>
                      <IconMessage />
                    </Box>
                  }
                  onClick={openContactUs}
                >
                  Contact Us
                </Button>
              </Box>

              <Divider color="decorativeBorderLight" />

              <Group visibleFrom="md">
                <Button
                  variant="transparent"
                  p={0}
                  h="auto"
                  aria-label="Visit our Facebook page"
                  component="a"
                  href="https://www.facebook.com/atlantawebdesign"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={iconFacebook}
                    alt=""
                    width={24}
                    height={24}
                    style={{ display: "block" }}
                  />
                </Button>

                <Button
                  variant="transparent"
                  p={0}
                  h="auto"
                  aria-label="Visit our LinkedIn page"
                  component="a"
                  href="https://www.linkedin.com/company/airtight-design"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={iconLinkedin}
                    alt=""
                    width={24}
                    height={24}
                    style={{ display: "block" }}
                  />
                </Button>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col
            span={{ base: 12, md: "content" }}
            style={{ alignSelf: "stretch" }}
          >
            <Stack w="100%" justify="space-between" h="100%">
              <Stack gap="sm" w="100%" mt={{ base: 0, md: "xl", lg: "xxxl" }}>
                {contactInfo.map((item) => (
                  <React.Fragment key={item.label}>
                    <Stack gap="xs" w="100%" hiddenFrom="md" align="center">
                      <Text c="fontLight" fw="700" size="xs">
                        {item.label}
                      </Text>
                      <Text
                        c="fontLightSecondary"
                        size="xs"
                        ta="center"
                        {...(item.href && { component: "a", href: item.href })}
                      >
                        {item.value}
                      </Text>
                    </Stack>

                    <Group
                      w="100%"
                      gap="xl"
                      align="flex-start"
                      wrap="nowrap"
                      visibleFrom="md"
                    >
                      <Text
                        w={{ base: 80, md: 60, lg: 80 }}
                        c="fontLight"
                        fw="700"
                        size="xs"
                        ta="right"
                      >
                        {item.label}
                      </Text>
                      <Text
                        c="fontLightSecondary"
                        size="xs"
                        {...(item.href && { component: "a", href: item.href })}
                      >
                        {item.value}
                      </Text>
                    </Group>
                  </React.Fragment>
                ))}
              </Stack>

              <Divider color="decorativeBorderLight" hiddenFrom="md" />

              <Group justify="center" hiddenFrom="md">
                <Button
                  variant="transparent"
                  p={0}
                  h="auto"
                  aria-label="Visit our Facebook page"
                  component="a"
                  href="https://www.facebook.com/atlantawebdesign"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={iconFacebook}
                    alt=""
                    width={24}
                    height={24}
                    style={{ display: "block" }}
                  />
                </Button>

                <Button
                  variant="transparent"
                  p={0}
                  h="auto"
                  aria-label="Visit our LinkedIn page"
                  component="a"
                  href="https://www.linkedin.com/company/airtight-design"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={iconLinkedin}
                    alt=""
                    width={24}
                    height={24}
                    style={{ display: "block" }}
                  />
                </Button>
              </Group>

              <Group
                justify="center"
                gap="sm"
                mt={{ base: "lg", md: 40, lg: 60 }}
              >
                <Text
                  {...copyrightTextProps}
                  component={Link}
                  td="underline"
                  to="/sitemap"
                >
                  Sitemap
                </Text>
                <Text {...copyrightTextProps}>&middot;</Text>
                <Text
                  {...copyrightTextProps}
                  component={Link}
                  td="underline"
                  to="/privacy-policy"
                >
                  Privacy Policy
                </Text>
                <Text {...copyrightTextProps} visibleFrom="md">
                  &middot;
                </Text>
                <Text {...copyrightTextProps} visibleFrom="md">
                  &copy; {currentYear} Airtight Design.
                </Text>
              </Group>

              <Text {...copyrightTextProps} ta="center" hiddenFrom="md">
                &copy; {currentYear} Airtight Design.
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 4, md: 3, lg: 4 }} visibleFrom="md">
            <Center>
              <img
                src={logoVertical}
                alt="Airtight Design"
                width={150}
                style={{ display: "block" }}
              />
            </Center>
          </Grid.Col>
        </Grid>
      </Card>

      <ContactUsDrawer
        opened={contactUsOpened}
        onClose={closeContactUs}
        onCloseStack={closeContactUs}
      />
    </>
  );
}
